import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import Lightbox from './components/Lightbox';
import ProductCard from './components/ProductCard';
import { products } from '../../mocks/products';
import * as transPt from '../../i18n/local/pt/common';
import * as transEn from '../../i18n/local/en/common';
import * as transEs from '../../i18n/local/es/common';
import * as transDe from '../../i18n/local/de/common';
import * as transIt from '../../i18n/local/it/common';
import * as transZh from '../../i18n/local/zh/common';

const translationsMap = {
  pt: transPt,
  en: transEn,
  es: transEs,
  de: transDe,
  it: transIt,
  zh: transZh,
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const currentLang = i18n.language;
  const trans = translationsMap[currentLang as keyof typeof translationsMap] || translationsMap.pt;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = document.body;
    const opt = {
      margin: 6,
      filename: 'proposta-moviis.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    (window as any).html2pdf().set(opt).from(element).save();
  };

  const handleWhatsApp = () => {
    const url = window.location.href;
    const text = encodeURIComponent(`${trans.actions.whatsappText} ${url}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  };

  const handleEmail = () => {
    const url = window.location.href;
    const subject = encodeURIComponent(trans.actions.emailSubject);
    const body = encodeURIComponent(`${trans.actions.emailBody} ${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-start justify-start">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/edited_f9ca6a6f0db3fcbb7721dbd51cad55a5_d50120be.jpg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-6 z-10">
          <h1 className="text-2xl font-bold">
            <img 
              src="https://static.readdy.ai/image/bf3288118a626d850d67549d7636b7c2/3dcdbb178e5fb905220d9ae2fca35ce8.png" 
              alt="MOVIIS" 
              className="h-10 brightness-0 invert"
            />
          </h1>
          <LanguageSelector />
        </header>

        {/* Hero Content */}
        <div className="relative z-10 text-left text-white px-8 pt-48 w-full max-w-4xl">
          <h2 className="text-5xl md:text-6xl font-bold mb-6">{t('hero.title')}</h2>
          <p className="text-xl md:text-2xl mb-8">{t('hero.subtitle')}</p>
          <a 
            href="#produtos"
            className="inline-block bg-[#21C25E] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#1da84e] transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('hero.button')}
          </a>
        </div>
      </section>

      {/* Products Section */}
      <section id="produtos" className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, idx) => (
              <ProductCard 
                key={product.id}
                product={product}
                index={idx + 1}
                onImageClick={() => setLightboxImage(product.image)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Total Bar */}
      <div className="bg-gray-900 text-white py-6 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="text-xl font-bold">{t('total.label')}</span>
          <span className="text-2xl font-bold text-[#21C25E] uppercase tracking-wide">
            {trans.total.onRequest}
          </span>
        </div>
      </div>

      {/* Delivery Conditions */}
      <section className="bg-gray-50 py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Shipping */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-ship-line text-2xl text-[#21C25E]"></i>
              </div>
              <div className="text-xs font-bold text-gray-600 uppercase mb-2">{trans.delivery.shipping.label}</div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{trans.delivery.shipping.value}</div>
              <div className="text-sm text-gray-600">{trans.delivery.shipping.description}</div>
            </div>

            {/* Delivery */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-truck-line text-2xl text-[#21C25E]"></i>
              </div>
              <div className="text-xs font-bold text-gray-600 uppercase mb-2">{trans.delivery.delivery.label}</div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{trans.delivery.delivery.value}</div>
              <div className="text-sm text-gray-600">{trans.delivery.delivery.description}</div>
            </div>

            {/* Packaging */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-box-3-line text-2xl text-[#21C25E]"></i>
              </div>
              <div className="text-xs font-bold text-gray-600 uppercase mb-2">{trans.delivery.packaging.label}</div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{trans.delivery.packaging.value}</div>
              <div className="text-sm text-gray-600">{trans.delivery.packaging.description}</div>
            </div>

            {/* Warranty */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-[#21C25E]"></i>
              </div>
              <div className="text-xs font-bold text-gray-600 uppercase mb-2">{trans.delivery.warranty.label}</div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{trans.delivery.warranty.value}</div>
              <div className="text-sm text-gray-600">{trans.delivery.warranty.description}</div>
              <div className="text-xs text-gray-500 mt-1">{trans.delivery.warranty.note}</div>
            </div>

            {/* Manufacturer - Full Width */}
            <div className="bg-[#21C25E] p-6 rounded-lg shadow-sm md:col-span-2 lg:col-span-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#21C25E] border-2 border-white rounded-lg flex items-center justify-center">
                    <i className="ri-building-2-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white uppercase mb-1">{trans.delivery.manufacturer.label}</div>
                    <div className="text-2xl font-bold text-white">{trans.delivery.manufacturer.value}</div>
                  </div>
                </div>
                <a 
                  href="https://www.bltwfitness.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white text-[#21C25E] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {trans.delivery.manufacturer.link}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Summary */}
      <section className="bg-white py-16 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gray-900 text-white rounded-lg p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-700">
              <div>
                <div className="text-sm text-gray-400 mb-2">{trans.financial.title}</div>
                <div className="text-3xl font-bold text-[#21C25E] uppercase tracking-wide">
                  {trans.total.onRequest}
                </div>
              </div>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-3xl text-[#21C25E]"></i>
              </div>
            </div>

            {/* Installment 1 */}
            <div className="mb-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-3 h-3 bg-[#21C25E] rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1">{trans.financial.installment1.title}</div>
                  <div className="text-sm text-gray-400 mb-2">{trans.financial.installment1.description}</div>
                  <div className="text-lg font-semibold text-[#21C25E] uppercase tracking-wide">
                    {trans.total.onRequest}
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 ml-7">
                <div className="bg-[#21C25E] h-2 rounded-full w-[30%]"></div>
              </div>
            </div>

            {/* Installment 2 */}
            <div className="mb-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-3 h-3 bg-[#86efac] rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1">{trans.financial.installment2.title}</div>
                  <div className="text-sm text-gray-400 mb-2">{trans.financial.installment2.description}</div>
                  <div className="text-lg font-semibold text-[#86efac] uppercase tracking-wide">
                    {trans.total.onRequest}
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 ml-7">
                <div className="bg-[#86efac] h-2 rounded-full w-[65%]"></div>
              </div>
            </div>

            {/* Installment 3 */}
            <div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 bg-white/30 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1">{trans.financial.installment3.title}</div>
                  <div className="text-sm text-gray-400 mb-2">{trans.financial.installment3.description}</div>
                  <div className="text-lg font-semibold text-green-400 uppercase tracking-wide">
                    {trans.total.onRequest}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Considerations */}
      <section className="bg-white py-16 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#f0fdf4] border-l-4 border-[#21C25E] rounded-lg p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#21C25E] rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{trans.considerations.title}</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#21C25E] mt-1">•</span>
                    <span>{trans.considerations.validity}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#21C25E] mt-1">•</span>
                    <span>{trans.considerations.deadline}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#21C25E] mt-1">•</span>
                    <span>{trans.considerations.observation}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#21C25E] mt-1">•</span>
                    <span>{trans.considerations.assembly}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <section className="bg-white py-12 px-4 md:px-8 print-hide">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 bg-[#AAFF00] text-black px-6 py-4 rounded-lg font-semibold hover:bg-[#99ee00] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line text-xl"></i>
              {trans.actions.print}
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center justify-center gap-2 bg-[#AAFF00] text-black px-6 py-4 rounded-lg font-semibold hover:bg-[#99ee00] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-download-line text-xl"></i>
              {trans.actions.download}
            </button>
            <button
              onClick={handleWhatsApp}
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 rounded-lg font-semibold hover:bg-[#20ba5a] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              {trans.actions.whatsapp}
            </button>
            <button
              onClick={handleEmail}
              className="flex items-center justify-center gap-2 bg-[#EA4335] text-white px-6 py-4 rounded-lg font-semibold hover:bg-[#d33426] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line text-xl"></i>
              {trans.actions.email}
            </button>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-gray-50 py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Meeting Card */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-8 rounded-lg">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">{trans.contact.meeting.title}</h3>
              <a
                href="https://outlook.office.com/book/Importaodiretadeequipamentosparaacademia@moviis.com.br/?ismsaljsauthenabled=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {trans.contact.meeting.button}
              </a>
            </div>

            {/* Email Card */}
            <div className="bg-[#21C25E] text-white p-8 rounded-lg">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">{trans.contact.email.title}</h3>
              <a
                href="mailto:lp@moviis.com.br"
                className="inline-block bg-white text-[#21C25E] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {trans.contact.email.button}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Column 1 - Brand */}
            <div>
              <img 
                src="https://static.readdy.ai/image/bf3288118a626d850d67549d7636b7c2/3dcdbb178e5fb905220d9ae2fca35ce8.png" 
                alt="MOVIIS" 
                className="h-8 brightness-0 invert mb-4"
              />
              <p className="text-gray-400 text-sm mb-4">{trans.footer.description}</p>
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/company/moviis/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/moviis__/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://api.whatsapp.com/send/?phone=5511996408888" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>

            {/* Column 2 - Equipment */}
            <div>
              <h4 className="font-bold text-lg mb-4">{trans.footer.equipment.title}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#produtos" className="hover:text-white transition-colors cursor-pointer">{trans.footer.equipment.cardio}</a></li>
                <li><a href="#produtos" className="hover:text-white transition-colors cursor-pointer">{trans.footer.equipment.strength}</a></li>
                <li><a href="#produtos" className="hover:text-white transition-colors cursor-pointer">{trans.footer.equipment.functional}</a></li>
              </ul>
            </div>

            {/* Column 3 - Information */}
            <div>
              <h4 className="font-bold text-lg mb-4">{trans.footer.info.title}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#produtos" className="hover:text-white transition-colors cursor-pointer">{trans.footer.info.catalog}</a></li>
                <li>{trans.footer.info.shipping}</li>
                <li>{trans.footer.info.delivery}</li>
                <li>{trans.footer.info.warranty}</li>
              </ul>
            </div>

            {/* Column 4 - Contact */}
            <div>
              <h4 className="font-bold text-lg mb-4">{trans.footer.contactInfo.title}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>{trans.footer.contactInfo.address}</li>
                <li>{trans.footer.contactInfo.zipcode}</li>
                <li><a href="tel:+551132552877" className="hover:text-white transition-colors cursor-pointer">{trans.footer.contactInfo.phone}</a></li>
                <li><a href="mailto:lp@moviis.com.br" className="hover:text-white transition-colors cursor-pointer">{trans.footer.contactInfo.email}</a></li>
                <li className="pt-2">{trans.footer.contactInfo.hours}</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <div>{trans.footer.copyright}</div>
            <div>{trans.footer.poweredBy}</div>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox 
          image={lightboxImage} 
          onClose={() => setLightboxImage(null)} 
        />
      )}
    </div>
  );
}